import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { log } from "../lib/log";

const STRINGS = {
  en: {
    // Brand
    brand: "Bala Basava Movement",
    brand_sub: "Reach a Child. Shape a Life.",
    brand_overline: "BALA BASAVA MOVEMENT",

    // Nav
    nav_schools: "Schools",
    nav_how: "How it Works",
    nav_impact: "Impact",
    nav_signin: "Sign in",
    nav_donate: "Sponsor Books",
    nav_dashboard: "Dashboard",
    nav_signout: "Sign out",

    // Hero
    hero_overline: "ಬಾಲ ಬಸವ · VACHANAS FOR YOUNG HEARTS",
    hero_title_1: "Why Bala Basava",
    hero_title_em: "Comics for Children?",
    hero_fomo: "If Basava's vachanas don't reach them early, they may never reach them at all.",
    hero_body: "Most rural children never meet Basavanna in a format they love. By the time adulthood arrives, the window has closed.\n\nA comic, a vachana, a voice — placed early — can change a child's life.",
    hero_vachana: "ಸ್ಥಾವರಕ್ಕಳಿವುಂಟು, ಜಂಗಮಕ್ಕಳಿವಿಲ್ಲ!",
    hero_vachana_en: "What is built may fall. Living wisdom never does.",
    hero_cta_browse: "Browse Schools",
    hero_cta_how: "How it Works",
    hero_cta_comics: "Explore Comics",
    comics_modal_title: "Bala Basava Comics",
    comics_modal_sub: "A preview of the stories delivered to every funded classroom.",
    comics_modal_close: "Close",
    comics_empty: "Comics will be added by admin soon.",

    // Stats
    stat_books: "Books delivered",
    stat_students: "Children reached",
    stat_schools: "Schools active",
    stat_given: "given",

    // Value prop
    vp_overline: "THE OFFER",
    vp_title_1: "Place a comic of",
    vp_title_em: "courage, equality, and vachana wisdom",
    vp_title_2: "in every child's hand.",
    vp_body: "Twelve illustrated stories adapted from Basavanna's life and vachanas. Children don't read duty into it — they read adventure, friendship, fairness. The philosophy lands on its own.",
    vp_kannada: "Kannada",
    vp_kannada_body: "Original tongue, original cadence.",
    vp_english: "English",
    vp_english_body: "Bilingual so every classroom joins in.",
    vp_audio: "Audio",
    vp_audio_body: "QR-linked narration for read-aloud time.",
    vp_closer: "Children don't learn philosophy from lectures. They learn it from stories — repeated, illustrated, heard, told back.",

    // How it works
    how_overline: "HOW IT WORKS",
    how_title: "Four steps. No middle layer. No guesswork.",
    how_1_title: "Schools request",
    how_1_body: "Register, upload a class list, submit.",
    how_2_title: "Sponsors purchase book sets",
    how_2_body: "Payments are verified on our server. Minimum 20 sets per sponsorship.",
    how_3_title: "We print, ship & enable learning",
    how_3_body: "Batch printed, tracked to the school. Teachers get Train-the-Trainer coaching to use the comics in class.",
    how_4_title: "Proof lands in your dashboard",
    how_4_body: "Photos and videos from distribution day appear in your dashboard.",
    how_full: "Read the full process →",

    // Impact dashboard
    impact_overline: "IMPACT DASHBOARD",
    impact_title: "Real impact, publicly visible.",
    impact_sub: "Every book delivered. Every child reached. Transparently tracked.",
    impact_books: "Books Delivered",
    impact_children: "Children Reached",
    impact_schools: "Schools Active",

    // Trust
    trust_overline: "WHY TRUST US",
    trust_title: "Stories that shape values early.",
    trust_line: "",
    trust_payments_title: "Trusted Payments",
    trust_payments_body: "All purchases are processed through Razorpay — a secure and trusted payment gateway. Every transaction is verified on backend systems before confirmation.",
    trust_proof_title: "Proof upload by schools",
    trust_proof_body: "Schools upload photos & videos from distribution day — attached to the same sponsorship.",
    trust_mod_title: "Admin moderation",
    trust_mod_body: "Every proof is reviewed before it's shown. No stock photos, no reruns.",
    trust_price_title: "Transparent pricing",
    trust_price_body: "One flat price per set. Pricing changes are versioned; past sponsorships keep their original snapshot.",
    trust_80g_title: "Tax Benefit for Donors",
    trust_80g_body1: "Contributions are processed through AAPTI Society (Registered Non-Profit).",
    trust_80g_body2: "Eligible donors can claim tax deduction under Section 80G of the Income Tax Act.",
    trust_80g_body3: "An official 80G receipt will be issued after successful contribution.",
    trust_80g_disclaimer: "Tax benefits are subject to applicable laws and donor eligibility.",

    // Schools section
    schools_overline_tag: "WAITING RIGHT NOW",
    schools_title: "Real classrooms waiting now.",
    schools_body: "Each card is a verified school with a locked student count. Every day unfunded is a day of stories undelivered.",
    schools_all: "See all schools →",
    schools_none: "No approved schools waiting — new ones onboard every week.",

    // Impact feed
    feed_overline: "LIVE, VERIFIED",
    feed_title: "Impact Feed.",
    feed_body: "Every line below was verified on our backend systems before it was published. Anonymous donors stay anonymous. Dedications are kept verbatim.",
    feed_empty: "No verified contributions yet. Be the first line on this feed.",
    feed_funded: "funded",
    feed_sets: "sets for",

    // Final CTA
    final_overline: "ONE DECISION AWAY",
    final_title_1: "The next child who reads Basava",
    final_title_em: "depends on someone acting today.",
    final_body: "Not next quarter. Not the next CSR cycle. Today — because childhood does not wait for calendars.",
    final_cta_donate: "Sponsor Books for a School",
    final_cta_school: "Register your school",

    // Sponsor / Purchase page
    donate_monthly: "Make this a monthly sponsorship",
    donate_monthly_hint: "We'll remind you each month. You can cancel anytime.",
    donate_secure_note: "Your sponsorship is securely processed via Razorpay and verified on our backend systems.",
    donate_80g_note: "Secure payment via Razorpay. This is a purchase of educational books for distribution.",
    donate_gateway_note: "Secure payments. No gateway-specific data stored.",
    sponsor_purchase_note: "Each sponsorship represents the purchase of Bala Basava comic sets for distribution to students.",
    business_entity_line: "Bala Basava is an educational publishing initiative under Analytical and Creative Studio OPC Pvt Ltd.",
    payment_trust_line: "Secure payment via Razorpay. This is a purchase of educational books for distribution.",

    // Success
    success_title: "Your book sponsorship has been confirmed.",
    success_body: "A confirmation email with your sponsorship details has been sent to you.",
    success_80g: "",
    success_receipt_note: "Confirmation will be sent to your registered email.",

    // Donor dashboard
    pledges_tab: "Monthly pledges",
    certificate: "Certificate",

    // Footer
    footer_heading: "Basavanna's 12th-century vachanas, placed in children's hands — in the tongue they dream in.",
    footer_body: "Rooted in Karnataka. Committed to early exposure. Kannada, English, and audio — because values land deepest when they arrive young.",
    footer_platform: "PLATFORM",
    footer_trust: "WHY IT'S TRUSTABLE",
    footer_tag: "Transparent · Verified · Early",

    // Videos
    videos_title: "Impact Videos",
    videos_sub: "Real classroom moments — children reading, listening, and reciting.",
    videos_none: "No videos yet — check back soon.",
    videos_load_more: "Load more",

    // MoU
    mou_badge: "MoU Available",
    mou_section: "MoU Document",
    mou_view: "View MoU",
    mou_view_title: "Memorandum of Understanding",
    mou_view_sub: "Signed agreement between the school and Bala Basava Movement.",
    mou_close: "Close",
    mou_none: "MoU not yet uploaded.",

    // Donor impact updates
    impact_updates_title: "Your Impact Updates",
    impact_updates_sub: "Photos & videos from the schools you supported.",
    impact_updates_empty: "No updates yet — proofs will appear here once your schools upload them.",
    impact_delivery_confirmed: "Delivery confirmed",
    impact_delivery_pending: "Delivery pending",

    // Share
    share_impact: "Share Impact",
    share_title: "Share the movement",
    share_sub: "No personal details are shared. Only the cause.",
    share_whatsapp: "WhatsApp",
    share_copy: "Copy link",
    share_system: "More options",
    share_copied: "Message copied!",
    share_message: "I sponsored Bala Basava educational comics for children — helping them experience Basavanna's vachanas early in life.\n\nStories that shape values early.\n\nJoin this movement:\nhttps://balabasava.com",

    // Distribution visit scheduling
    visits_tab: "Visits",
    visits_toggle_label: "I would like to personally participate in book distribution",
    visits_toggle_hint: "Turn this on to schedule an in-person visit to schools you have supported.",
    visits_schedule_heading: "Schedule a distribution visit",
    visits_schedule_sub: "Available Mon–Sat, 10:00 AM – 3:00 PM. Only schools you have supported are listed.",
    visits_field_school: "Select school",
    visits_field_school_ph: "Choose a school…",
    visits_field_date: "Select date",
    visits_field_time: "Select time",
    visits_field_time_ph: "Choose a time slot…",
    visits_field_notes: "Notes (optional)",
    visits_field_notes_ph: "Any message for the school or admin?",
    visits_btn_schedule: "Schedule Visit",
    visits_btn_reschedule: "Reschedule",
    visits_btn_cancel: "Cancel",
    visits_btn_save: "Save new date",
    visits_btn_close: "Close",
    visits_list_title: "Your Scheduled Visits",
    visits_list_empty: "No visits scheduled yet.",
    visits_status_scheduled: "Scheduled",
    visits_status_rescheduled: "Rescheduled",
    visits_status_completed: "Completed",
    visits_status_cancelled: "Cancelled",
    visits_no_schools: "You have not sponsored any school yet — once a sponsorship is verified, schools will appear here.",
    visits_cancel_confirm: "Cancel this visit?",
    visits_toast_scheduled: "Visit scheduled!",
    visits_toast_rescheduled: "Visit rescheduled!",
    visits_toast_cancelled: "Visit cancelled.",
    visits_toast_select: "Please pick a school, date and time.",
    visits_reschedule_title: "Reschedule your visit",
    visits_reschedule_sub: "Pick a new weekday between 10:00 AM and 3:00 PM.",

    // Language toggle label
    lang_toggle: "ಕನ್ನಡ",

    // Welcome page
    w_overline: "THE MOVEMENT",
    w_title_1: "Bala Basava",
    w_title_2: "Movement",
    w_tagline: "For those who believe in the values of Basavanna.",
    w_intro_1: "For generations, Basavanna's vachanas have guided how we think, work, and live.",
    w_intro_2a: "Now, the responsibility is ours —",
    w_intro_2b: "to ensure these values reach our children.",
    w_identity_1: "If we have grown with these values…",
    w_identity_2: "they deserve to grow with them too.",
    w_fomo_highlight: "If vachanas don't reach them early,",
    w_fomo_rest: "something else will shape their thinking.",
    w_cta_explore_comics: "Explore Comics",
    w_cta_start_audio: "Start Audio Experience",
    w_cta_support_school: "Support a School",
    w_trust_line: "Stories that carry values forward — from one generation to the next.",

    w_intro_body_1: "Bala Basava brings the vachanas of Basavanna into children's lives through stories.",
    w_intro_body_2a: "Instead of teaching values through lectures, we use comics and audio that children",
    w_intro_body_2b: "enjoy, remember, and live.",

    w_path_comics_tag: "COMICS",
    w_path_comics_title: "Learn Through Stories",
    w_path_comics_body: "Children understand values best through stories they relate to.",
    w_path_comics_cta: "View Comics",
    w_path_audio_tag: "AUDIO",
    w_path_audio_title: "Listen & Remember",
    w_path_audio_body: "Audio vachanas help children hear, repeat, and internalize values.",
    w_path_audio_cta: "Start Audio",
    w_path_growth_tag: "GROWTH",
    w_path_growth_title: "Help a Child Grow with Values",
    w_path_growth_body: "Bring these stories to children who may never experience them.",
    w_path_growth_cta: "Support a School",

    w_basavanna_overline: "A 12TH CENTURY REFORMER",
    w_basavanna_title: "Who Was Basavanna?",
    w_basavanna_image_caption: "12th-century Karnataka",
    w_basavanna_p1a: "Basavanna (Lord Basaveshwara) was a 12th-century philosopher, poet, and social reformer who didn't just teach values — he",
    w_basavanna_p1b: "redesigned society itself.",
    w_basavanna_p2a: "He created",
    w_basavanna_p2b: "Anubhava Mantapa",
    w_basavanna_p2c: " — one of the world's earliest democratic forums where people from all backgrounds spoke freely and equally.",
    w_he_introduced: "HE INTRODUCED",
    w_kayakave: "Kayakave Kailasa",
    w_kayakave_sub: "— Work is Worship",
    w_benefit_1: "gave dignity to labour",
    w_benefit_2: "encouraged self-reliance",
    w_benefit_3: "reduced inequality",
    w_benefit_4: "enabled economic progress",
    w_never_stay: "Basavanna's vachanas were never meant to stay in books.",
    w_meant_to_shape: "They were meant to shape how people think, act, and live.",
    w_question_label: "But here is the question",
    w_question_1: "If these ideas transformed society then…",
    w_question_2: "why are they not reaching children now?",

    w_early_overline: "EARLY YEARS · LASTING IMPACT",
    w_early_title: "Why Children Must Learn Vachanas Early",
    w_early_image_caption: "The early years shape everything",
    w_early_absorb_1: "Children don't wait to learn values.",
    w_early_absorb_2: "They absorb whatever reaches them first.",
    w_if_reach_early: "IF VACHANAS REACH THEM EARLY",
    w_reach_1: "They develop clarity",
    w_reach_2: "They understand right and wrong",
    w_reach_3: "They respect work",
    w_reach_4: "They grow with honesty",
    w_if_they_dont: "IF THEY DON'T",
    w_dont_1: "Their thinking is shaped by random influences",
    w_dont_2: "Habits form without guidance",
    w_fofo: "\"What is not learned early…\nis rarely lived later.\"",
    w_petro_intro: "A small story today shapes decades of thinking.",
    w_resources_label: "Resources",
    w_resources_body: "build economies",
    w_values_label: "Values",
    w_values_body: "build civilizations",
    w_children_learn: "Children learn through:",
    w_learn_a1: "Stories",
    w_learn_b1: "they enjoy",
    w_learn_a2: "Voices",
    w_learn_b2: "they hear",
    w_learn_a3: "Moments",
    w_learn_b3: "they remember",
    w_neuro_close_1: "Bala Basava brings vachanas through",
    w_neuro_close_2: "comics and audio",
    w_neuro_close_3: "so values become part of who they are.",

    w_comics_overline: "5 VOLUMES · ILLUSTRATED FOR CHILDREN",
    w_comics_title: "Bala Basava Comics",
    w_comics_sub: "Available on trusted book platforms",
    w_comics_trust: "Available on trusted book platforms for secure purchase and delivery.",

    w_audio_overline: "AUDIO EXPERIENCE",
    w_audio_title: "Experience Through Audio",
    w_audio_body: "Children remember what they hear repeatedly.",

    w_final_overline: "THE PATH",
    w_final_title: "Three Paths. One Purpose.",
    w_final_learn: "Learn",
    w_final_learn_sub: "through stories",
    w_final_experience: "Experience",
    w_final_experience_sub: "through listening",
    w_final_grow: "Grow",
    w_final_grow_sub: "with values",
  },
  kn: {
    brand: "ಬಾಲ ಬಸವ ಚಳವಳಿ",
    brand_sub: "ಪ್ರತಿ ಮಗುವಿಗೆ ಬಸವಣ್ಣರ ವಚನಗಳು",
    brand_overline: "ಬಾಲ ಬಸವ ಚಳವಳಿ",

    nav_schools: "ಶಾಲೆಗಳು",
    nav_how: "ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ",
    nav_impact: "ಪ್ರಭಾವ",
    nav_signin: "ಸೈನ್ ಇನ್",
    nav_donate: "ಪುಸ್ತಕ ಪ್ರಾಯೋಜಿಸಿ",
    nav_dashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    nav_signout: "ಸೈನ್ ಔಟ್",

    hero_overline: "ಬಾಲ ಬಸವ · ಮಕ್ಕಳ ಹೃದಯಕ್ಕೆ ವಚನಗಳು",
    hero_title_1: "ಏಕೆ ಬಾಲ ಬಸವ",
    hero_title_em: "ಮಕ್ಕಳಿಗೆ ಕಾಮಿಕ್‌ಗಳು?",
    hero_fomo: "ಬಸವಣ್ಣರ ವಚನಗಳು ಬಾಲ್ಯದಲ್ಲೇ ತಲುಪದಿದ್ದರೆ, ಅವು ಜೀವಮಾನ ತಲುಪದೇ ಉಳಿಯಬಹುದು.",
    hero_body: "ಹೆಚ್ಚಿನ ಗ್ರಾಮೀಣ ಮಕ್ಕಳು ತಮಗಿಷ್ಟವಾಗುವ ರೂಪದಲ್ಲಿ ಬಸವಣ್ಣರ ಪರಿಚಯ ಪಡೆಯುವುದಿಲ್ಲ. ವಯಸ್ಸಾಗುವ ಹೊತ್ತಿಗೆ ಅವಕಾಶದ ಬಾಗಿಲು ಮುಚ್ಚಿಹೋಗಿರುತ್ತದೆ.\n\nಕಾಮಿಕ್, ವಚನ, ಧ್ವನಿ — ಬಾಲ್ಯದಲ್ಲೇ ನೀಡಿದರೆ — ಒಂದು ಬದುಕನ್ನು ಬದಲಾಯಿಸಬಹುದು.",
    hero_vachana: "ಸ್ಥಾವರಕ್ಕಳಿವುಂಟು, ಜಂಗಮಕ್ಕಳಿವಿಲ್ಲ!",
    hero_vachana_en: "ಕಟ್ಟಿದ್ದು ಬೀಳಬಹುದು. ಜಂಗಮ ಜ್ಞಾನ ಅಳಿವಿಲ್ಲ.",
    hero_cta_browse: "ಶಾಲೆಗಳನ್ನು ನೋಡಿ",
    hero_cta_how: "ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ",
    hero_cta_comics: "ಕಾಮಿಕ್‌ಗಳನ್ನು ನೋಡಿ",
    comics_modal_title: "ಬಾಲ ಬಸವ ಕಾಮಿಕ್‌ಗಳು",
    comics_modal_sub: "ಪ್ರತಿ ನಿಧಿಯಾಕೃತ ತರಗತಿಗೆ ತಲುಪುವ ಕಥೆಗಳ ಪೂರ್ವನೋಟ.",
    comics_modal_close: "ಮುಚ್ಚಿ",
    comics_empty: "ಅಡ್ಮಿನ್ ಶೀಘ್ರದಲ್ಲಿ ಕಾಮಿಕ್‌ಗಳನ್ನು ಸೇರಿಸುತ್ತಾರೆ.",

    stat_books: "ತಲುಪಿದ ಪುಸ್ತಕಗಳು",
    stat_students: "ತಲುಪಿದ ಮಕ್ಕಳು",
    stat_schools: "ಸಕ್ರಿಯ ಶಾಲೆಗಳು",
    stat_given: "ನೀಡಲಾಗಿದೆ",

    vp_overline: "ಕೊಡುಗೆ",
    vp_title_1: "ಪ್ರತಿ ಮಗುವಿನ ಕೈಗೆ",
    vp_title_em: "ಧೈರ್ಯ, ಸಮಾನತೆ, ವಚನ ಜ್ಞಾನ",
    vp_title_2: "ದ ಕಾಮಿಕ್ ತಲುಪಿಸೋಣ.",
    vp_body: "ಬಸವಣ್ಣರ ಬದುಕು ಮತ್ತು ವಚನಗಳಿಂದ ಆಯ್ದ ಹನ್ನೆರಡು ಚಿತ್ರಕಥೆಗಳು. ಮಕ್ಕಳು ಇವನ್ನು ಕರ್ತವ್ಯದಂತೆ ಓದುವುದಿಲ್ಲ — ಸಾಹಸ, ಸ್ನೇಹ, ನ್ಯಾಯವಾಗಿ ಓದುತ್ತಾರೆ. ತತ್ವಶಾಸ್ತ್ರ ಸ್ವತಃ ಇಳಿಯುತ್ತದೆ.",
    vp_kannada: "ಕನ್ನಡ",
    vp_kannada_body: "ಮೂಲ ಭಾಷೆ, ಮೂಲ ಲಯ.",
    vp_english: "ಇಂಗ್ಲಿಷ್",
    vp_english_body: "ಪ್ರತಿ ತರಗತಿಯೂ ಸೇರುವಂತೆ ಎರಡು ಭಾಷೆ.",
    vp_audio: "ಆಡಿಯೋ",
    vp_audio_body: "ಓದಿ ಕೇಳಿಸಲು QR-ಲಿಂಕ್ ಧ್ವನಿ.",
    vp_closer: "ಮಕ್ಕಳು ತತ್ವಶಾಸ್ತ್ರವನ್ನು ಪಾಠಗಳಿಂದ ಕಲಿಯುವುದಿಲ್ಲ — ಪುನಃ ಪುನಃ ಕಥೆಯಿಂದ ಕಲಿಯುತ್ತಾರೆ.",

    how_overline: "ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ",
    how_title: "ನಾಲ್ಕು ಹಂತಗಳು. ಮಧ್ಯವರ್ತಿಯಿಲ್ಲ. ಸಂಶಯವಿಲ್ಲ.",
    how_1_title: "ಶಾಲೆ ಕೇಳುತ್ತದೆ",
    how_1_body: "ನೋಂದಾಯಿಸಿ, ತರಗತಿ ಪಟ್ಟಿ ಅಪ್ಲೋಡ್ ಮಾಡಿ, ಸಲ್ಲಿಸಿ.",
    how_2_title: "ಪ್ರಾಯೋಜಕರು ಪುಸ್ತಕ ಸೆಟ್ ಖರೀದಿಸುತ್ತಾರೆ",
    how_2_body: "ಪಾವತಿಗಳನ್ನು ನಮ್ಮ ಸರ್ವರ್‌ನಲ್ಲಿ ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ. ಪ್ರತಿ ಪ್ರಾಯೋಜನೆಗೆ ಕನಿಷ್ಠ 20 ಸೆಟ್.",
    how_3_title: "ಮುದ್ರಣ, ಸಾಗಣೆ ಮತ್ತು ಕಲಿಕೆ",
    how_3_body: "ಬ್ಯಾಚ್ ಮುದ್ರಣ, ಶಾಲೆಗೆ ಟ್ರ್ಯಾಕ್ ಸಾಗಣೆ. ಶಿಕ್ಷಕರಿಗೆ Train-the-Trainer ತರಬೇತಿ — ಕಾಮಿಕ್‌ಗಳನ್ನು ತರಗತಿಯಲ್ಲಿ ಬಳಸಲು.",
    how_4_title: "ಪುರಾವೆ ನಿಮ್ಮ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಬರುತ್ತದೆ",
    how_4_body: "ವಿತರಣಾ ದಿನದ ಫೋಟೋ ಮತ್ತು ವೀಡಿಯೋಗಳು ನಿಮ್ಮ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ನಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.",
    how_full: "ಪೂರ್ಣ ಪ್ರಕ್ರಿಯೆ ಓದಿ →",

    impact_overline: "ಪ್ರಭಾವ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    impact_title: "ನೈಜ ಪ್ರಭಾವ, ಸಾರ್ವಜನಿಕವಾಗಿ ಗೋಚರ.",
    impact_sub: "ಪ್ರತಿ ಪುಸ್ತಕ ತಲುಪಿದೆ. ಪ್ರತಿ ಮಗು ತಲುಪಿದೆ. ಪಾರದರ್ಶಕವಾಗಿ ಗುರುತಿಸಲಾಗಿದೆ.",
    impact_books: "ತಲುಪಿದ ಪುಸ್ತಕಗಳು",
    impact_children: "ತಲುಪಿದ ಮಕ್ಕಳು",
    impact_schools: "ಸಕ್ರಿಯ ಶಾಲೆಗಳು",

    trust_overline: "ನಮ್ಮ ಮೇಲೆ ಏಕೆ ನಂಬಿಕೆ",
    trust_title: "ಚಿಕ್ಕ ವಯಸ್ಸಿನಲ್ಲೇ ಮೌಲ್ಯ ರೂಪಿಸುವ ಕಥೆಗಳು.",
    trust_line: "",
    trust_payments_title: "ವಿಶ್ವಾಸಾರ್ಹ ಪಾವತಿಗಳು",
    trust_payments_body: "ಎಲ್ಲ ಖರೀದಿಗಳು Razorpay — ಸುರಕ್ಷಿತ ಮತ್ತು ವಿಶ್ವಾಸಾರ್ಹ ಪಾವತಿ ವ್ಯವಸ್ಥೆಯ ಮೂಲಕ ಸಂಸ್ಕರಿಸಲ್ಪಡುತ್ತವೆ. ದೃಢೀಕರಣದ ಮೊದಲು ಪ್ರತಿ ವಹಿವಾಟು ಬ್ಯಾಕೆಂಡ್‌ನಲ್ಲಿ ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ.",
    trust_proof_title: "ಶಾಲೆಗಳಿಂದ ಪುರಾವೆ ಅಪ್ಲೋಡ್",
    trust_proof_body: "ವಿತರಣಾ ದಿನದ ಫೋಟೋ, ವೀಡಿಯೋಗಳನ್ನು ಶಾಲೆಗಳು ಅಪ್ಲೋಡ್ ಮಾಡುತ್ತವೆ.",
    trust_mod_title: "ಅಡ್ಮಿನ್ ಪರಿಶೀಲನೆ",
    trust_mod_body: "ಪ್ರತಿ ಪುರಾವೆ ತೋರಿಸುವ ಮೊದಲು ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ.",
    trust_price_title: "ಪಾರದರ್ಶಕ ಬೆಲೆ",
    trust_price_body: "ಒಂದೇ ಬೆಲೆ ಪ್ರತಿ ಸೆಟ್‌ಗೆ. ಹಿಂದಿನ ಪ್ರಾಯೋಜನೆಗಳಿಗೆ ಮೂಲ ಸ್ನ್ಯಾಪ್‌ಶಾಟ್ ಉಳಿಯುತ್ತದೆ.",
    trust_80g_title: "ದಾನಿಗಳಿಗೆ ತೆರಿಗೆ ವಿನಾಯಿತಿ",
    trust_80g_body1: "ಕೊಡುಗೆಗಳನ್ನು AAPTI Society (ನೋಂದಾಯಿತ ಲಾಭೋದ್ದೇಶರಹಿತ) ಮೂಲಕ ಸಂಸ್ಕರಿಸಲಾಗುತ್ತದೆ.",
    trust_80g_body2: "ಅರ್ಹ ದಾನಿಗಳು ಆದಾಯ ತೆರಿಗೆ ಕಾಯ್ದೆಯ ಸೆಕ್ಷನ್ 80G ಅಡಿ ತೆರಿಗೆ ವಿನಾಯಿತಿ ಪಡೆಯಬಹುದು.",
    trust_80g_body3: "ಯಶಸ್ವಿ ಕೊಡುಗೆ ನಂತರ ಅಧಿಕೃತ 80G ರಶೀದಿ ನೀಡಲಾಗುತ್ತದೆ.",
    trust_80g_disclaimer: "ತೆರಿಗೆ ಪ್ರಯೋಜನಗಳು ಅನ್ವಯವಾಗುವ ಕಾನೂನುಗಳಿಗೆ ಮತ್ತು ದಾನಿಯ ಅರ್ಹತೆಗೆ ಒಳಪಟ್ಟಿರುತ್ತವೆ.",

    schools_overline_tag: "ಈಗ ಕಾಯುತ್ತಿವೆ",
    schools_title: "ನೈಜ ತರಗತಿಗಳು ಈಗ ಕಾಯುತ್ತಿವೆ.",
    schools_body: "ಪ್ರತಿ ಕಾರ್ಡ್ ಒಂದು ಪರಿಶೀಲಿತ ಶಾಲೆ. ನಿಧಿ ಇಲ್ಲದ ಪ್ರತಿ ದಿನ ಕಥೆಗಳಿಲ್ಲದ ದಿನ.",
    schools_all: "ಎಲ್ಲ ಶಾಲೆಗಳು →",
    schools_none: "ಇನ್ನೂ ಅನುಮೋದಿತ ಶಾಲೆಗಳಿಲ್ಲ — ಪ್ರತಿ ವಾರ ಹೊಸವು ಸೇರಿಸುತ್ತೇವೆ.",

    feed_overline: "ಲೈವ್ · ಪರಿಶೀಲಿತ",
    feed_title: "ಪ್ರಭಾವ ಫೀಡ್.",
    feed_body: "ಕೆಳಗಿನ ಪ್ರತಿಯೊಂದೂ ಸಾಲು ಪ್ರಕಟಿಸುವ ಮೊದಲು ಬ್ಯಾಕೆಂಡ್‌ನಲ್ಲಿ ಪರಿಶೀಲಿಸಲಾಗಿದೆ.",
    feed_empty: "ಇನ್ನೂ ಪರಿಶೀಲಿತ ಪ್ರಾಯೋಜನೆ ಇಲ್ಲ. ಈ ಫೀಡ್‌ನಲ್ಲಿ ಮೊದಲ ಸಾಲು ಆಗಿ.",
    feed_funded: "ಪ್ರಾಯೋಜಿಸಿದರು",
    feed_sets: "ಸೆಟ್‌ಗಳು, ಶಾಲೆ",

    final_overline: "ಒಂದೇ ನಿರ್ಧಾರ",
    final_title_1: "ಮುಂದಿನ ಬಸವಣ್ಣರನ್ನು ಓದುವ ಮಗು",
    final_title_em: "ಇಂದು ಯಾರಾದರೂ ನಿರ್ಧರಿಸುವುದರ ಮೇಲೆ ಅವಲಂಬಿಸಿದೆ.",
    final_body: "ಮುಂದಿನ ತ್ರೈಮಾಸಿಕವಲ್ಲ. ಮುಂದಿನ CSR ಚಕ್ರವಲ್ಲ. ಇಂದು — ಬಾಲ್ಯ ಕ್ಯಾಲೆಂಡರ್ ಕಾಯುವುದಿಲ್ಲ.",
    final_cta_donate: "ಶಾಲೆಗಾಗಿ ಪುಸ್ತಕ ಪ್ರಾಯೋಜಿಸಿ",
    final_cta_school: "ನಿಮ್ಮ ಶಾಲೆಯನ್ನು ನೋಂದಾಯಿಸಿ",

    donate_monthly: "ಮಾಸಿಕ ಪ್ರಾಯೋಜನೆ ಮಾಡಿ",
    donate_monthly_hint: "ಪ್ರತಿ ತಿಂಗಳು ನೆನಪಿಸುತ್ತೇವೆ. ಯಾವಾಗ ಬೇಕಾದರೂ ರದ್ದುಗೊಳಿಸಬಹುದು.",
    donate_secure_note: "ನಿಮ್ಮ ಪ್ರಾಯೋಜನೆ Razorpay ಮೂಲಕ ಸುರಕ್ಷಿತವಾಗಿ ಸಂಸ್ಕರಿಸಿ ಬ್ಯಾಕೆಂಡ್‌ನಲ್ಲಿ ಪರಿಶೀಲಿಸಲಾಗುತ್ತದೆ.",
    donate_80g_note: "Razorpay ಮೂಲಕ ಸುರಕ್ಷಿತ ಪಾವತಿ. ಇದು ವಿತರಣೆಗಾಗಿ ಶೈಕ್ಷಣಿಕ ಪುಸ್ತಕಗಳ ಖರೀದಿ.",
    donate_gateway_note: "ಸುರಕ್ಷಿತ ಪಾವತಿಗಳು.",
    sponsor_purchase_note: "ಪ್ರತಿ ಪ್ರಾಯೋಜನೆಯು ವಿದ್ಯಾರ್ಥಿಗಳಿಗೆ ವಿತರಣೆಗಾಗಿ ಬಾಲ ಬಸವ ಕಾಮಿಕ್ ಸೆಟ್‌ಗಳ ಖರೀದಿಯನ್ನು ಪ್ರತಿನಿಧಿಸುತ್ತದೆ.",
    business_entity_line: "ಬಾಲ ಬಸವವು Analytical and Creative Studio OPC Pvt Ltd ಅಡಿಯಲ್ಲಿ ಒಂದು ಶೈಕ್ಷಣಿಕ ಪ್ರಕಾಶನ ಉಪಕ್ರಮ.",
    payment_trust_line: "Razorpay ಮೂಲಕ ಸುರಕ್ಷಿತ ಪಾವತಿ. ಇದು ವಿತರಣೆಗಾಗಿ ಶೈಕ್ಷಣಿಕ ಪುಸ್ತಕಗಳ ಖರೀದಿ.",

    success_title: "ನಿಮ್ಮ ಪುಸ್ತಕ ಪ್ರಾಯೋಜನೆ ದೃಢಪಟ್ಟಿದೆ.",
    success_body: "ನಿಮ್ಮ ಪ್ರಾಯೋಜನೆಯ ವಿವರಗಳೊಂದಿಗೆ ದೃಢೀಕರಣ ಇಮೇಲ್ ಕಳುಹಿಸಲಾಗಿದೆ.",
    success_80g: "",
    success_receipt_note: "ದೃಢೀಕರಣ ನಿಮ್ಮ ನೋಂದಾಯಿತ ಇಮೇಲ್‌ಗೆ ಕಳುಹಿಸಲಾಗುತ್ತದೆ.",

    pledges_tab: "ಮಾಸಿಕ ಪ್ರತಿಜ್ಞೆಗಳು",
    certificate: "ಪ್ರಮಾಣಪತ್ರ",

    footer_heading: "ಬಸವಣ್ಣರ 12ನೇ ಶತಮಾನದ ವಚನಗಳು, ಮಕ್ಕಳು ಕನಸು ಕಾಣುವ ಭಾಷೆಯಲ್ಲಿ ಅವರ ಕೈಗೆ.",
    footer_body: "ಕರ್ನಾಟಕದಲ್ಲಿ ಬೇರೂರಿದೆ. ಬಾಲ್ಯದ ಒಡ್ಡುವಿಕೆಗೆ ಬದ್ಧ. ಕನ್ನಡ, ಇಂಗ್ಲಿಷ್, ಧ್ವನಿ — ಚಿಕ್ಕ ವಯಸ್ಸಿನಲ್ಲಿ ಬಂದರೆ ಮೌಲ್ಯ ಆಳಕ್ಕೆ ಇಳಿಯುತ್ತದೆ.",
    footer_platform: "ವೇದಿಕೆ",
    footer_trust: "ಏಕೆ ವಿಶ್ವಾಸಾರ್ಹ",
    footer_tag: "ಪಾರದರ್ಶಕ · ಪರಿಶೀಲಿತ · ಬಾಲ್ಯದಿಂದಲೇ",

    videos_title: "ಪ್ರಭಾವ ವೀಡಿಯೋಗಳು",
    videos_sub: "ನೈಜ ತರಗತಿ ಕ್ಷಣಗಳು — ಮಕ್ಕಳು ಓದುವುದು, ಕೇಳುವುದು, ಪುನರುಚ್ಚಾರಣೆ.",
    videos_none: "ಇನ್ನೂ ವೀಡಿಯೋಗಳಿಲ್ಲ.",
    videos_load_more: "ಇನ್ನೂ ಲೋಡ್ ಮಾಡಿ",

    mou_badge: "MoU ಲಭ್ಯ",
    mou_section: "MoU ದಾಖಲೆ",
    mou_view: "MoU ನೋಡಿ",
    mou_view_title: "ಒಪ್ಪಂದ ಪತ್ರ (MoU)",
    mou_view_sub: "ಶಾಲೆ ಮತ್ತು ಬಾಲ ಬಸವ ಚಳವಳಿಯ ನಡುವಿನ ಒಪ್ಪಂದ.",
    mou_close: "ಮುಚ್ಚಿ",
    mou_none: "MoU ಇನ್ನೂ ಅಪ್ಲೋಡ್ ಮಾಡಿಲ್ಲ.",

    impact_updates_title: "ನಿಮ್ಮ ಪ್ರಭಾವದ ನವೀಕರಣಗಳು",
    impact_updates_sub: "ನೀವು ಬೆಂಬಲಿಸಿದ ಶಾಲೆಗಳ ಫೋಟೋ ಮತ್ತು ವೀಡಿಯೋಗಳು.",
    impact_updates_empty: "ಇನ್ನೂ ನವೀಕರಣಗಳಿಲ್ಲ — ನಿಮ್ಮ ಶಾಲೆಗಳು ಪುರಾವೆ ಅಪ್ಲೋಡ್ ಮಾಡಿದಾಗ ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.",
    impact_delivery_confirmed: "ವಿತರಣೆ ದೃಢೀಕೃತ",
    impact_delivery_pending: "ವಿತರಣೆ ಬಾಕಿ",

    share_impact: "ಪ್ರಭಾವ ಹಂಚಿಕೊಳ್ಳಿ",
    share_title: "ಚಳವಳಿಯನ್ನು ಹಂಚಿಕೊಳ್ಳಿ",
    share_sub: "ವೈಯಕ್ತಿಕ ವಿವರಗಳನ್ನು ಹಂಚುವುದಿಲ್ಲ. ಉದ್ದೇಶ ಮಾತ್ರ.",
    share_whatsapp: "WhatsApp",
    share_copy: "ಲಿಂಕ್ ನಕಲಿಸಿ",
    share_system: "ಹೆಚ್ಚಿನ ಆಯ್ಕೆಗಳು",
    share_copied: "ಸಂದೇಶ ನಕಲಿಸಲಾಗಿದೆ!",
    share_message: "ನಾನು ಮಕ್ಕಳಿಗೆ ಬಾಲ ಬಸವ ಶೈಕ್ಷಣಿಕ ಕಾಮಿಕ್ಸ್ ಪ್ರಾಯೋಜಿಸಿದ್ದೇನೆ — ಬಸವಣ್ಣರ ವಚನಗಳು ಮಕ್ಕಳಿಗೆ ಚಿಕ್ಕ ವಯಸ್ಸಿನಲ್ಲೇ ತಲುಪಲಿ ಎಂಬ ಆಶಯ.\n\nಚಿಕ್ಕ ವಯಸ್ಸಿನಲ್ಲೇ ಮೌಲ್ಯ ರೂಪಿಸುವ ಕಥೆಗಳು.\n\nನೀವು ಸಹ ಸೇರಿ:\nhttps://balabasava.com",

    visits_tab: "ಭೇಟಿಗಳು",
    visits_toggle_label: "ನಾನು ಖುದ್ದಾಗಿ ಪುಸ್ತಕ ವಿತರಣೆಯಲ್ಲಿ ಭಾಗವಹಿಸಲು ಬಯಸುತ್ತೇನೆ",
    visits_toggle_hint: "ನೀವು ಬೆಂಬಲಿಸಿದ ಶಾಲೆಗಳಿಗೆ ಭೇಟಿ ನಿಗದಿಪಡಿಸಲು ಇದನ್ನು ಆನ್ ಮಾಡಿ.",
    visits_schedule_heading: "ಭೇಟಿ ನಿಗದಿ ಮಾಡಿ",
    visits_schedule_sub: "ಸೋಮವಾರ–ಶನಿವಾರ, ಬೆಳಿಗ್ಗೆ 10:00 – ಮಧ್ಯಾಹ್ನ 3:00. ನೀವು ಬೆಂಬಲಿಸಿದ ಶಾಲೆಗಳು ಮಾತ್ರ ಲಭ್ಯ.",
    visits_field_school: "ಶಾಲೆ ಆಯ್ಕೆ ಮಾಡಿ",
    visits_field_school_ph: "ಶಾಲೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ…",
    visits_field_date: "ದಿನಾಂಕ",
    visits_field_time: "ಸಮಯ",
    visits_field_time_ph: "ಸಮಯ ಸ್ಲಾಟ್ ಆಯ್ಕೆಮಾಡಿ…",
    visits_field_notes: "ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)",
    visits_field_notes_ph: "ಶಾಲೆಗೆ ಅಥವಾ ಅಡ್ಮಿನ್‌ಗೆ ಯಾವುದೇ ಸಂದೇಶ?",
    visits_btn_schedule: "ಭೇಟಿ ನಿಗದಿ ಮಾಡಿ",
    visits_btn_reschedule: "ಪುನಃ ನಿಗದಿ ಮಾಡಿ",
    visits_btn_cancel: "ರದ್ದುಗೊಳಿಸಿ",
    visits_btn_save: "ಹೊಸ ದಿನಾಂಕ ಉಳಿಸಿ",
    visits_btn_close: "ಮುಚ್ಚಿ",
    visits_list_title: "ನಿಮ್ಮ ನಿಗದಿತ ಭೇಟಿಗಳು",
    visits_list_empty: "ಇನ್ನೂ ಭೇಟಿ ನಿಗದಿಪಡಿಸಿಲ್ಲ.",
    visits_status_scheduled: "ನಿಗದಿತ",
    visits_status_rescheduled: "ಪುನಃ ನಿಗದಿತ",
    visits_status_completed: "ಪೂರ್ಣಗೊಂಡಿದೆ",
    visits_status_cancelled: "ರದ್ದುಗೊಳಿಸಲಾಗಿದೆ",
    visits_no_schools: "ನೀವಿನ್ನೂ ಯಾವ ಶಾಲೆಯನ್ನೂ ಪ್ರಾಯೋಜಿಸಿಲ್ಲ — ಪ್ರಾಯೋಜನೆ ದೃಢೀಕರಿಸಿದ ನಂತರ ಶಾಲೆಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.",
    visits_cancel_confirm: "ಈ ಭೇಟಿಯನ್ನು ರದ್ದುಗೊಳಿಸಬೇಕೇ?",
    visits_toast_scheduled: "ಭೇಟಿ ನಿಗದಿಯಾಗಿದೆ!",
    visits_toast_rescheduled: "ಭೇಟಿ ಪುನಃ ನಿಗದಿಯಾಗಿದೆ!",
    visits_toast_cancelled: "ಭೇಟಿ ರದ್ದುಗೊಳಿಸಲಾಗಿದೆ.",
    visits_toast_select: "ಶಾಲೆ, ದಿನಾಂಕ ಮತ್ತು ಸಮಯ ಆಯ್ಕೆಮಾಡಿ.",
    visits_reschedule_title: "ಭೇಟಿಯನ್ನು ಪುನಃ ನಿಗದಿ ಮಾಡಿ",
    visits_reschedule_sub: "ಬೆಳಿಗ್ಗೆ 10:00 – ಮಧ್ಯಾಹ್ನ 3:00 ನಡುವೆ ವಾರದ ದಿನ ಆಯ್ಕೆ ಮಾಡಿ.",

    lang_toggle: "English",

    // Welcome page — Kannada
    w_overline: "ಚಳುವಳಿ",
    w_title_1: "ಬಾಲ ಬಸವ",
    w_title_2: "ಚಳುವಳಿ",
    w_tagline: "ಬಸವಣ್ಣನ ಮೌಲ್ಯಗಳಲ್ಲಿ ನಂಬಿಕೆ ಇಡುವವರಿಗಾಗಿ.",
    w_intro_1: "ತಲೆಮಾರುಗಳಿಂದ ಬಸವಣ್ಣನ ವಚನಗಳು ನಾವು ಹೇಗೆ ಯೋಚಿಸಬೇಕು, ಕೆಲಸ ಮಾಡಬೇಕು ಮತ್ತು ಬದುಕಬೇಕು ಎಂಬುದನ್ನು ಮಾರ್ಗದರ್ಶಿಸಿವೆ.",
    w_intro_2a: "ಈಗ ಜವಾಬ್ದಾರಿ ನಮ್ಮದು —",
    w_intro_2b: "ಈ ಮೌಲ್ಯಗಳನ್ನು ನಮ್ಮ ಮಕ್ಕಳಿಗೆ ತಲುಪಿಸುವ ಜವಾಬ್ದಾರಿ ನಮ್ಮದು.",
    w_identity_1: "ನಾವು ಈ ಮೌಲ್ಯಗಳೊಂದಿಗೆ ಬೆಳೆದಿದ್ದೇವೆಂದರೆ…",
    w_identity_2: "ಅವರೂ ಇವುಗಳೊಂದಿಗೆ ಬೆಳೆಯಲು ಅರ್ಹರು.",
    w_fomo_highlight: "ವಚನಗಳು ಅವರಿಗೆ ಬೇಗ ತಲುಪದಿದ್ದರೆ,",
    w_fomo_rest: "ಅವರ ಚಿಂತನೆಗೆ ಬೇರೆ ಏನೋ ಆಕಾರ ನೀಡುತ್ತದೆ.",
    w_cta_explore_comics: "ಕಾಮಿಕ್ಸ್ ನೋಡಿ",
    w_cta_start_audio: "ಆಡಿಯೋ ಅನುಭವ ಆರಂಭಿಸಿ",
    w_cta_support_school: "ಶಾಲೆಗೆ ಸಹಾಯ ಮಾಡಿ",
    w_trust_line: "ಕಥೆಗಳು ಮೌಲ್ಯಗಳನ್ನು ಮುಂದೆ ಸಾಗಿಸುತ್ತವೆ — ಒಂದು ಪೀಳಿಗೆಯಿಂದ ಮುಂದಿನದಕ್ಕೆ.",

    w_intro_body_1: "ಬಾಲ ಬಸವ, ಬಸವಣ್ಣನ ವಚನಗಳನ್ನು ಕಥೆಗಳ ಮೂಲಕ ಮಕ್ಕಳ ಜೀವನಕ್ಕೆ ತರುತ್ತದೆ.",
    w_intro_body_2a: "ಉಪನ್ಯಾಸಗಳ ಮೂಲಕ ಮೌಲ್ಯಗಳನ್ನು ಕಲಿಸುವ ಬದಲು, ಮಕ್ಕಳು",
    w_intro_body_2b: "ಇಷ್ಟಪಡುವ, ನೆನಪಿಡುವ ಮತ್ತು ಬದುಕುವ ಕಾಮಿಕ್ಸ್ ಹಾಗೂ ಆಡಿಯೋ ಬಳಸುತ್ತೇವೆ.",

    w_path_comics_tag: "ಕಾಮಿಕ್ಸ್",
    w_path_comics_title: "ಕಥೆಗಳ ಮೂಲಕ ಕಲಿಯಿರಿ",
    w_path_comics_body: "ಮಕ್ಕಳಿಗೆ ಸಂಬಂಧಿಸಬಲ್ಲ ಕಥೆಗಳ ಮೂಲಕ ಮೌಲ್ಯಗಳು ಉತ್ತಮವಾಗಿ ಅರ್ಥವಾಗುತ್ತವೆ.",
    w_path_comics_cta: "ಕಾಮಿಕ್ಸ್ ನೋಡಿ",
    w_path_audio_tag: "ಆಡಿಯೋ",
    w_path_audio_title: "ಆಲಿಸಿ ಮತ್ತು ನೆನಪಿಡಿ",
    w_path_audio_body: "ಆಡಿಯೋ ವಚನಗಳು ಮಕ್ಕಳಿಗೆ ಕೇಳಿ, ಪುನರಾವೃತ್ತಿ ಮಾಡಿ, ಆಂತರಿಕಗೊಳಿಸಲು ನೆರವಾಗುತ್ತವೆ.",
    w_path_audio_cta: "ಆಡಿಯೋ ಆರಂಭಿಸಿ",
    w_path_growth_tag: "ಬೆಳವಣಿಗೆ",
    w_path_growth_title: "ಮಕ್ಕಳು ಮೌಲ್ಯಗಳೊಂದಿಗೆ ಬೆಳೆಯಲು ನೆರವಾಗಿ",
    w_path_growth_body: "ಈ ಕಥೆಗಳನ್ನು ಎಂದೂ ಕೇಳದ ಮಕ್ಕಳಿಗೆ ತಲುಪಿಸಿ.",
    w_path_growth_cta: "ಶಾಲೆಗೆ ಸಹಾಯ ಮಾಡಿ",

    w_basavanna_overline: "12ನೇ ಶತಮಾನದ ಸಮಾಜ ಸುಧಾರಕ",
    w_basavanna_title: "ಬಸವಣ್ಣ ಯಾರು?",
    w_basavanna_image_caption: "12ನೇ ಶತಮಾನದ ಕರ್ನಾಟಕ",
    w_basavanna_p1a: "ಬಸವಣ್ಣ (ಬಸವೇಶ್ವರ) 12ನೇ ಶತಮಾನದ ತತ್ವಜ್ಞಾನಿ, ಕವಿ ಮತ್ತು ಸಮಾಜ ಸುಧಾರಕರು — ಅವರು ಕೇವಲ ಮೌಲ್ಯಗಳನ್ನು ಬೋಧಿಸಲಿಲ್ಲ; ಅವರು",
    w_basavanna_p1b: "ಸಮಾಜವನ್ನೇ ಮರುವಿನ್ಯಾಸಗೊಳಿಸಿದರು.",
    w_basavanna_p2a: "ಅವರು",
    w_basavanna_p2b: "ಅನುಭವ ಮಂಟಪ",
    w_basavanna_p2c: " ಅನ್ನು ಸ್ಥಾಪಿಸಿದರು — ವಿಶ್ವದ ಮೊದಲ ಪ್ರಜಾಸತ್ತಾತ್ಮಕ ವೇದಿಕೆಗಳಲ್ಲೊಂದು, ಅಲ್ಲಿ ಎಲ್ಲಾ ವರ್ಗದ ಜನರು ಮುಕ್ತವಾಗಿ ಮತ್ತು ಸಮಾನವಾಗಿ ಮಾತನಾಡಿದರು.",
    w_he_introduced: "ಅವರು ಪರಿಚಯಿಸಿದ್ದು",
    w_kayakave: "ಕಾಯಕವೇ ಕೈಲಾಸ",
    w_kayakave_sub: "— ಕೆಲಸವೇ ಪೂಜೆ",
    w_benefit_1: "ಶ್ರಮಕ್ಕೆ ಘನತೆ ನೀಡಿತು",
    w_benefit_2: "ಸ್ವಾವಲಂಬನೆಯನ್ನು ಪ್ರೋತ್ಸಾಹಿಸಿತು",
    w_benefit_3: "ಅಸಮಾನತೆಯನ್ನು ಕಡಿಮೆಗೊಳಿಸಿತು",
    w_benefit_4: "ಆರ್ಥಿಕ ಪ್ರಗತಿಯನ್ನು ಸಾಧ್ಯವಾಗಿಸಿತು",
    w_never_stay: "ಬಸವಣ್ಣನ ವಚನಗಳು ಪುಸ್ತಕಗಳಲ್ಲಿ ಉಳಿಯಲು ರಚಿತವಾಗಿಲ್ಲ.",
    w_meant_to_shape: "ಜನರು ಹೇಗೆ ಯೋಚಿಸುತ್ತಾರೆ, ವರ್ತಿಸುತ್ತಾರೆ ಮತ್ತು ಬದುಕುತ್ತಾರೆ ಎಂಬುದನ್ನು ರೂಪಿಸಲು ಇವು ಸೃಷ್ಟಿಯಾದವು.",
    w_question_label: "ಆದರೆ ಪ್ರಶ್ನೆ ಇದು",
    w_question_1: "ಈ ವಿಚಾರಗಳು ಆಗ ಸಮಾಜವನ್ನು ಪರಿವರ್ತಿಸಿದ್ದರೆ…",
    w_question_2: "ಇಂದಿನ ಮಕ್ಕಳಿಗೆ ಅವು ಏಕೆ ತಲುಪುತ್ತಿಲ್ಲ?",

    w_early_overline: "ಬಾಲ್ಯ · ಶಾಶ್ವತ ಪರಿಣಾಮ",
    w_early_title: "ಮಕ್ಕಳು ವಚನಗಳನ್ನು ಬೇಗ ಕಲಿಯಬೇಕಾದ ಕಾರಣ",
    w_early_image_caption: "ಬಾಲ್ಯವೇ ಎಲ್ಲವನ್ನೂ ರೂಪಿಸುತ್ತದೆ",
    w_early_absorb_1: "ಮಕ್ಕಳು ಮೌಲ್ಯಗಳನ್ನು ಕಲಿಯಲು ಕಾಯುವುದಿಲ್ಲ.",
    w_early_absorb_2: "ಅವರಿಗೆ ಮೊದಲು ತಲುಪಿದ್ದನ್ನು ಹೀರಿಕೊಳ್ಳುತ್ತಾರೆ.",
    w_if_reach_early: "ವಚನಗಳು ಬೇಗ ತಲುಪಿದರೆ",
    w_reach_1: "ಅವರಿಗೆ ಸ್ಪಷ್ಟತೆ ಮೂಡುತ್ತದೆ",
    w_reach_2: "ಸರಿ-ತಪ್ಪು ಅರ್ಥವಾಗುತ್ತದೆ",
    w_reach_3: "ಕೆಲಸವನ್ನು ಗೌರವಿಸುತ್ತಾರೆ",
    w_reach_4: "ಸತ್ಯದೊಂದಿಗೆ ಬೆಳೆಯುತ್ತಾರೆ",
    w_if_they_dont: "ತಲುಪದಿದ್ದರೆ",
    w_dont_1: "ಅವರ ಚಿಂತನೆ ಯಾದೃಚ್ಛಿಕ ಪ್ರಭಾವಗಳಿಂದ ರೂಪುಗೊಳ್ಳುತ್ತದೆ",
    w_dont_2: "ಮಾರ್ಗದರ್ಶನವಿಲ್ಲದೆ ಅಭ್ಯಾಸಗಳು ಬೆಳೆಯುತ್ತವೆ",
    w_fofo: "\"ಬಾಲ್ಯದಲ್ಲಿ ಕಲಿತದ್ದೇ\nಜೀವನದಲ್ಲಿ ಉಳಿಯುತ್ತದೆ.\"",
    w_petro_intro: "ಇಂದಿನ ಸಣ್ಣ ಕಥೆ ದಶಕಗಳ ಚಿಂತನೆಯನ್ನು ರೂಪಿಸುತ್ತದೆ.",
    w_resources_label: "ಸಂಪನ್ಮೂಲಗಳು",
    w_resources_body: "ಆರ್ಥಿಕತೆಯನ್ನು ನಿರ್ಮಿಸುತ್ತವೆ",
    w_values_label: "ಮೌಲ್ಯಗಳು",
    w_values_body: "ಸಮಾಜವನ್ನು ನಿರ್ಮಿಸುತ್ತವೆ",
    w_children_learn: "ಮಕ್ಕಳು ಕಲಿಯುವುದು:",
    w_learn_a1: "ಕಥೆಗಳು",
    w_learn_b1: "ಅವರು ಆನಂದಿಸುವ",
    w_learn_a2: "ಧ್ವನಿಗಳು",
    w_learn_b2: "ಅವರು ಕೇಳುವ",
    w_learn_a3: "ಕ್ಷಣಗಳು",
    w_learn_b3: "ಅವರು ನೆನಪಿಡುವ",
    w_neuro_close_1: "ಬಾಲ ಬಸವ ವಚನಗಳನ್ನು",
    w_neuro_close_2: "ಕಾಮಿಕ್ಸ್ ಮತ್ತು ಆಡಿಯೋ",
    w_neuro_close_3: "ಮೂಲಕ ತರುತ್ತದೆ, ಇದರಿಂದ ಮೌಲ್ಯಗಳು ಅವರ ಭಾಗವಾಗುತ್ತವೆ.",

    w_comics_overline: "5 ಸಂಪುಟಗಳು · ಮಕ್ಕಳಿಗಾಗಿ ಚಿತ್ರೀಕರಿಸಿದ",
    w_comics_title: "ಬಾಲ ಬಸವ ಕಾಮಿಕ್ಸ್",
    w_comics_sub: "ವಿಶ್ವಾಸಾರ್ಹ ಪುಸ್ತಕ ವೇದಿಕೆಗಳಲ್ಲಿ ಲಭ್ಯ",
    w_comics_trust: "ಸುರಕ್ಷಿತ ಖರೀದಿ ಮತ್ತು ವಿತರಣೆಗೆ ವಿಶ್ವಾಸಾರ್ಹ ಪುಸ್ತಕ ವೇದಿಕೆಗಳಲ್ಲಿ ಲಭ್ಯ.",

    w_audio_overline: "ಆಡಿಯೋ ಅನುಭವ",
    w_audio_title: "ಆಡಿಯೋ ಮೂಲಕ ಅನುಭವಿಸಿ",
    w_audio_body: "ಮಕ್ಕಳು ಪುನಃ ಪುನಃ ಕೇಳಿದ್ದನ್ನು ನೆನಪಿನಲ್ಲಿಟ್ಟುಕೊಳ್ಳುತ್ತಾರೆ.",

    w_final_overline: "ಮಾರ್ಗ",
    w_final_title: "ಮೂರು ಮಾರ್ಗಗಳು. ಒಂದು ಗುರಿ.",
    w_final_learn: "ಕಲಿಯಿರಿ",
    w_final_learn_sub: "ಕಥೆಗಳ ಮೂಲಕ",
    w_final_experience: "ಅನುಭವಿಸಿ",
    w_final_experience_sub: "ಆಲಿಸುವ ಮೂಲಕ",
    w_final_grow: "ಬೆಳೆಯಿರಿ",
    w_final_grow_sub: "ಮೌಲ್ಯಗಳೊಂದಿಗೆ",
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const LS_KEY = "language_preference";

export function I18nProvider({ children }) {
  // language_preference is a public UI locale choice (not an auth token) — safe in localStorage
  const [lang, setLangState] = useState(() => localStorage.getItem(LS_KEY) || "en");
  const [forcedLang, setForcedLang] = useState(null);
  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem(LS_KEY, l); }
    catch (err) { log.warn("[i18n] could not persist language:", err?.message || err); }
  }, []);
  const activeLang = forcedLang || lang;
  const t = useCallback((k) => STRINGS[activeLang]?.[k] ?? STRINGS.en[k] ?? k, [activeLang]);
  useEffect(() => { document.documentElement.lang = activeLang; }, [activeLang]);

  const value = useMemo(
    () => ({ lang: activeLang, userLang: lang, setLang, t, setForcedLang }),
    [activeLang, lang, setLang, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);

// Forces a specific language for the lifetime of the component (dashboards).
// Reverts to user preference on unmount.
export function useForceLang(target) {
  const { setForcedLang } = useContext(I18nContext);
  useEffect(() => {
    setForcedLang(target);
    return () => setForcedLang(null);
  }, [target, setForcedLang]);
}
